import { useEffect, useState } from 'react';
import TopBanner from '@/components/back-office/TopBanner';
import { useRestaurantsContext } from '@/contexts/RestaurantContext';
import BOLayout from '@/pages/back-office/BOLayout';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import MagnifyingGlass from '@/assets/media/SVGComponents/MagnifyingGlass';
import Pagination from '@/components/back-office/Pagination';
import KDS from '@/components/KDS';
import axios from 'axios';
import useWebSocket from '@/hooks/useWebSocket';
import events from '@/constants/websocketEvents';
import { OrdersProvider } from '@/contexts/OrderContext';
import { Trash2, Search } from 'react-feather';
import Modal from '@/components/Modal';

const BOOrders = () => {
	const { selectedRestaurant, setSelectedRestaurant, refreshSelectedRestaurant } = useRestaurantsContext();
	const role = JSON.parse(localStorage.getItem('role'));
	const navigate = useNavigate();
	const [orders, setOrders] = useState([]);
	const [filteredOrders, setFilteredOrders] = useState([]);
	const [total, setTotal] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [viewMode, setViewMode] = useState('table');
	const [searchTerm, setSearchTerm] = useState('');
	const [searchType, setSearchType] = useState('reference');
	const ordersPerPage = 10;
	const [currentFilter, setCurrentFilter] = useState('all');
	const [pendingOrders, setPendingOrders] = useState([]);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [orderToDelete, setOrderToDelete] = useState(null);

	const statuses = {
		pending: {
			label: 'En attente de validation',
			bgColor: 'bg-[#FF8A00]',
		},
		validated: {
			label: 'Virement',
			bgColor: 'bg-[#02C65C]',
		},
		canceled: {
			label: 'Annulée',
			bgColor: 'bg-[#FF0000]',
		},
		pending_payment: {
			label: 'En attente de paiement',
			bgColor: 'bg-[#FF8A00]',
		},
		paid: {
			label: 'Payée',
			bgColor: 'bg-[#02C65C]',
		},
		declined: {
			label: 'Refusee',
			bgColor: 'bg-[#FF0000]',
		},
		processing: {
			label: 'En cours de traitement',
			bgColor: 'bg-[#FF8A00]',
		},
		completed: {
			label: 'Terminee',
			bgColor: 'bg-[#02C65C]',
		},
	};

	useEffect(() => {
		if (role && !['super-admin', 'admin'].includes(role)) {
			toast.info('Zone interdite', {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});

			navigate('/back-office');

			return;
		}
	}, [role]);

	// Fonction pour récupérer les informations à jour du restaurant
	const fetchRestaurantData = async () => {
		if (!selectedRestaurant || !selectedRestaurant.id) return;

		try {
			const response = await axios.get(
				`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${selectedRestaurant.id}/view`
			);

			if (response.status === 200 && response.data) {
				// Mettre à jour les informations du restaurant dans le contexte
				setSelectedRestaurant({
					...selectedRestaurant,
					...response.data,
				});

				console.log('Restaurant mis à jour:', response.data);
				console.log('Mode Haguim mis à jour:', response.data.is_haguim_mode);
			}
		} catch (error) {
			console.error('Erreur lors de la récupération des informations du restaurant:', error);
		}
	};

	const fetchData = async () => {
		if (!selectedRestaurant) return;

		try {
			// 1. Récupération de toutes les commandes (collection principale)
			const allRes = await axios.get(
				`${import.meta.env.VITE_API_URL}/api/back-office/orders/all`,
				{
					params: {
						restaurantId: selectedRestaurant.id,
					},
				}
			);

			if (allRes.status === 200) {
				const allOrders = allRes.data.orders || [];
				setOrders(allOrders);

				// Par défaut, on affiche toutes les commandes
				let filtered = allOrders;
				let totalItems = allOrders.length;

				// Si nous sommes sur le filtre "pending_payment", récupérer la collection temporaire
				if (currentFilter === 'pending_payment') {
					const pendingRes = await axios.get(
						`${import.meta.env.VITE_API_URL}/api/back-office/orders/all`,
						{
							params: {
								restaurantId: selectedRestaurant.id,
								status: 'pending_payment',
							},
						}
					);

					if (pendingRes.status === 200) {
						filtered = (pendingRes.data.orders || []).map((o) => ({ ...o, isTemporary: true }));
						totalItems = filtered.length;
						setPendingOrders(filtered);
					}
				}

				// Mise à jour des listes visibles
				setFilteredOrders(filtered);
				setTotal(totalItems);
			}
		} catch (error) {
			console.error(error);

			toast.error('Une erreur est survenue', {
				position: 'top-center',
				autoClose: 800,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});

			setTimeout(() => (window.location.href = '/back-office'), 1200);
		}
	};

	// Fonction pour supprimer une commande (manuel ou non)
	const deleteOrder = async (orderReference, isManual = false) => {
		try {
			const url = `${import.meta.env.VITE_API_URL}/api/back-office/${selectedRestaurant.id}/orders/${orderReference}/delete`;
			const response = await axios.delete(url, {
				params: isManual ? { isManual: true } : {},
			});

			if (response.status === 200) {
				toast.success('Commande supprimée avec succès', {
					position: 'top-center',
					autoClose: 1000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});

				// Rafraîchir la liste des commandes pour refléter la suppression
				await fetchData();
			}
		} catch (error) {
			console.error('Erreur lors de la suppression de la commande:', error);
			const errorMessage = error.response?.status === 403
				? "Vous n'avez pas les permissions nécessaires pour supprimer cette commande"
				: 'Erreur lors de la suppression de la commande';

			toast.error(errorMessage, {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});

			throw error; // Propager l'erreur pour que l'appelant puisse réagir
		}
	};

	// Fonction pour toggle le mode Haguim
	const toggleHaguimMode = async () => {
		if (!selectedRestaurant || !selectedRestaurant.id) return;

		try {
			const newHaguimMode = !selectedRestaurant.is_haguim_mode;

			const response = await axios.patch(
				`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${selectedRestaurant.id}/haguim-mode`,
				{
					is_haguim_mode: newHaguimMode
				}
			);

			if (response.status === 200) {
				// Rafraîchir les données du restaurant sélectionné depuis le backend
				await refreshSelectedRestaurant();

				toast.success(`Mode Haguim ${newHaguimMode ? 'activé' : 'désactivé'} avec succès`, {
					position: 'top-center',
					autoClose: 1000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});

				// Récupérer les nouvelles données
				fetchData();
			}
		} catch (error) {
			console.error('Erreur lors de la mise à jour du mode Haguim:', error);
			toast.error('Erreur lors de la mise à jour du mode Haguim', {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}
	};

	// Fonction pour filtrer les commandes
	const handleSearch = () => {
		if (!searchTerm.trim()) {
			if (currentFilter === 'pending_payment') {
				setFilteredOrders(pendingOrders);
				setTotal(pendingOrders.length);
			} else {
				setFilteredOrders(orders);
				setTotal(orders.length);
			}
			return;
		}

		const sourceOrders = currentFilter === 'pending_payment' ? pendingOrders : orders;

		const filtered = sourceOrders.filter(order => {
			const termLower = searchTerm.toLowerCase();

			switch (searchType) {
				case 'reference':
					return order.reference.toLowerCase().includes(termLower);
				case 'name':
					const fullName = `${order.delivery.customer.firstname} ${order.delivery.customer.lastname}`.toLowerCase();
					return fullName.includes(termLower);
				case 'email':
					const email = order.billing?.customer?.email?.toLowerCase() || '';
					return email.includes(termLower);
				case 'amount':
					if (!isNaN(parseFloat(searchTerm))) {
						const searchAmount = parseFloat(searchTerm);
						const orderAmount =
							parseFloat(order.total_amount) ||
							parseFloat(order.amount) ||
							parseFloat(order.totalAmount) ||
							parseFloat(order.total_price) ||
							parseFloat(order.products_amount) ||
							parseFloat(order.products_price) || 0;

						return Math.abs(orderAmount - searchAmount) < 0.01;
					}
					return false;
				default:
					return false;
			}
		});

		setFilteredOrders(filtered);
		setTotal(filtered.length);
		setCurrentPage(1);
	};

	useEffect(() => {
		handleSearch();
	}, [searchTerm, searchType, orders, pendingOrders, currentFilter]);

	useEffect(() => {
		fetchData();
		fetchRestaurantData();
	}, [selectedRestaurant?.id]);

	useEffect(() => {
		if (orders.length > 0) {
			console.log('Structure d\'une commande:', orders[0]);
		}
	}, [orders]);

	const indexOfLastOrder = currentPage * ordersPerPage;
	const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
	const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);

	const onPageChange = (pageNumber) => {
		setCurrentPage(pageNumber);
	};

	useWebSocket(events.ORDER_CREATED, fetchData);

	useEffect(() => {
		const checkIfTablet = () => {
			const isTablet = (window.innerWidth <= 1024 && window.innerWidth > 640) ||
				/iPad|Android|webOS|iPhone|iPod/.test(navigator.userAgent);

			if (isTablet) {
				setViewMode('kds');
			}
		};

		checkIfTablet();

		window.addEventListener('resize', checkIfTablet);

		return () => {
			window.removeEventListener('resize', checkIfTablet);
		};
	}, []);

	// Fonction pour filtrer les commandes en attente
	const showPendingOrders = async () => {
		try {
			// Vérifier si selectedRestaurant existe et a un ID
			if (!selectedRestaurant || !selectedRestaurant.id) {
				toast.error('Aucun restaurant sélectionné', {
					position: 'top-center',
					autoClose: 1000,
				});
				return;
			}

			// Le restaurantId est obligatoire pour le backend Firestore
			const response = await axios.get(
				`${import.meta.env.VITE_API_URL}/api/back-office/orders/all`,
				{
					params: {
						restaurantId: selectedRestaurant.id,
						status: 'pending_payment'
					}
				}
			);

			if (response.status === 200) {
				const pendingData = (response.data.orders || []).map(o => ({ ...o, isTemporary: true }));
				setPendingOrders(pendingData);
				setFilteredOrders(pendingData);
				setTotal(response.data.total || 0);
				setCurrentPage(1);
				setCurrentFilter('pending_payment');
			}
		} catch (error) {
			console.error("Erreur lors de la récupération des commandes en attente:", error);
			toast.error('Impossible de récupérer les commandes en attente', {
				position: 'top-center',
				autoClose: 1000,
			});
		}
	};

	const showAllOrders = () => {
		// Vérifier si nous avons des commandes
		if (orders && orders.length > 0) {
			setFilteredOrders(orders);
			setTotal(orders.length);
			setCurrentPage(1);
			setSearchTerm('');
			setCurrentFilter('all');
		} else {
			// Si pas de commandes, essayer de les récupérer
			fetchData();
			setCurrentFilter('all');
		}
	};

	const confirmDeleteTemporaryOrder = async () => {
		// Guard: ensure we have an order to delete
		if (!orderToDelete) return;

		// Store reference before any state updates to avoid accessing a cleared value later
		const { reference } = orderToDelete;

		try {
			await deleteOrder(reference, true);

			// Suppression réussie : on nettoie et on ferme la modal
			setPendingOrders(prev => prev.filter(order => order.reference !== reference));
			setShowDeleteModal(false);
			setOrderToDelete(null);
		} catch (error) {
			// L'erreur est déjà toastée dans deleteOrder ; garder la modal ouverte pour permettre une nouvelle tentative
			console.error('Erreur lors de la suppression de la commande:', error);
		}
	};

	return (
		<BOLayout>
			<div className="mb-12">
				<TopBanner
					role={role}
					showToggle={false}
					hasTopRightButton={false}
					hasEventInfo={false}
					withDescription={false}
					hasOtherButtons={false}
				/>
			</div>

			{window.innerWidth > 1024 && (
				<div className="flex justify-between items-center mb-4">
					<div className="text-md font-bold flex items-center gap-2">
						<span className="text-white w-8 h-8 rounded-full flex justify-center items-center bg-primary">
							{total}
						</span>
						Liste des commandes
					</div>

					<div className="flex items-center gap-2">
						<button
							onClick={toggleHaguimMode}
							className={`flex items-center gap-2 px-4 py-2 ${selectedRestaurant?.is_haguim_mode
									? 'bg-green-600 hover:bg-green-700'
									: 'bg-gray-500 hover:bg-gray-600'} 
								text-white rounded-md transition-colors`}
						>
							<span>Mode Haguim: {selectedRestaurant?.is_haguim_mode ? 'Activé' : 'Désactivé'}</span>
						</button>

						<button
							onClick={() => setViewMode(viewMode === 'table' ? 'kds' : 'table')}
							className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
						>
							<span>{viewMode === 'table' ? 'Vue KDS' : 'Vue Table'}</span>
						</button>
					</div>
				</div>
			)}

			{viewMode === 'table' ? (
				<>
					<div className="mb-4 flex items-center gap-4">
						<div className="relative flex-grow">
							<input
								type="text"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder="Rechercher..."
								className="w-full px-4 py-2 border border-gray-300 rounded-md pl-10"
							/>
							<span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
								<Search size={18} />
							</span>
						</div>
						<select
							value={searchType}
							onChange={(e) => setSearchType(e.target.value)}
							className="px-4 py-2 border border-gray-300 rounded-md"
						>
							<option value="reference">Référence</option>
							<option value="name">Nom</option>
							<option value="email">Email</option>
							<option value="amount">Montant</option>
						</select>
						<button
							onClick={showPendingOrders}
							className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors"
						>
							Afficher les commandes en attente
						</button>
						<button
							onClick={showAllOrders}
							className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
						>
							Afficher toutes les commandes
						</button>
					</div>

					<table className="table table-striped table-bordered table-auto w-full text-center">
						<thead className="border-b border-b-black pb-2 mb-4">
							<tr>
								<th></th>
								<th>Reference</th>
								<th>Nom du client</th>
								<th>Adresse de livraison</th>
								<th>Montant</th>
								<th>Statut</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{currentOrders.map((order, index) => {
								const orderAmount =
									order.total_amount ||
									order.amount ||
									order.totalAmount ||
									order.total_price ||
									order.products_amount ||
									order.products_price;

								const isTemporary = order.isTemporary === true;

								return (
									<tr key={order.reference || index}>
										<td
											className={`flex justify-center items-center w-8 h-8 rounded-full ${statuses[order.status]?.bgColor || 'bg-gray-200'}`}
										></td>
										<td>{order.reference}</td>
										<td>
											{order.delivery.customer.firstname} {order.delivery.customer.lastname}
										</td>
										<td>{order.delivery.address.address}</td>
										<td>{orderAmount ? `${orderAmount} €` : '-'}</td>
										<td>{statuses[order.status]?.label}</td>
										<td className="flex items-center justify-center gap-2">
											<button
												type="button"
												onClick={() =>
													navigate(`/back-office/restaurants/${selectedRestaurant.id}/commandes/${order.reference}/details`)
												}
											>
												<MagnifyingGlass color="#A2924A" width={40} height={40} />
											</button>
											{isTemporary && (
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														setOrderToDelete(order);
														setShowDeleteModal(true);
													}}
													className="ml-2 text-red-500 hover:text-red-700"
													title="Supprimer la commande temporaire"
												>
													<Trash2 size={20} />
												</button>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>

					<Pagination
						currentPage={currentPage}
						totalItems={total}
						itemsPerPage={ordersPerPage}
						onPageChange={onPageChange}
					/>
				</>
			) : (
				<div className="fixed inset-0 bg-white z-50 overflow-auto">
					{window.innerWidth > 1024 && !/iPad|Android|webOS|iPhone|iPod/.test(navigator.userAgent) && (
						<button
							onClick={() => setViewMode('table')}
							className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
						>
							X
						</button>
					)}
					<OrdersProvider>
						<KDS
							orders={filteredOrders}
							selectedRestaurantFromParent={selectedRestaurant}
						/>
					</OrdersProvider>
				</div>
			)}

			{filteredOrders.length === 0 && <div className="flex justify-center">Aucune commande</div>}

			{showDeleteModal && (
				<Modal
					setShowModal={setShowDeleteModal}
					type="danger"
					title="Confirmation de suppression"
					content={`Êtes-vous sûr de vouloir supprimer la commande ${orderToDelete?.reference} ?`}
					handleSubmit={confirmDeleteTemporaryOrder}
				/>
			)}
		</BOLayout>
	);
};

export default BOOrders;
